// Bank account history dashboard: assembles sticky top block + scrolling transactions.
// Settings (columns / modes / rows per page) persisted in localStorage via loadBankHistorySettings.
// "+ New transaction" and "+ Add cheque" share the NewTransactionDropdown.
// Download (CSV / Excel) and Print live in the toolbar via BankHistoryFilterBar.
// Print opens a styled serif sheet in a new tab and auto-triggers the browser print dialog.
// Inline edit mode: clicking a row opens EditTransactionRow with Save, Cancel, Edit, Delete.
// BackLink above the breadcrumb for easy return to the chart of accounts.

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import type { BankAccountHistoryData, BankHistoryFilter, AccountPickerItem, BankTransaction } from "../types";
import {
  BANK_HISTORY_COL_WIDTHS,
  DEFAULT_BANK_HISTORY_FILTER,
  getColumnLayout,
  loadBankHistorySettings,
  saveBankHistorySettings,
  type BankHistorySettings,
} from "../constants";
import { applyFilters, computeRunningBalances, fmtCurrency, getUniquePayees } from "../utils";
import { exportAsCsv, exportAsXlsx, type ExportRow } from "../exportUtils";
import { openPrintPreview, type PrintRow } from "../printUtils";
import BackLink from "../../../../../components/BackLink";
import AccountPickerDropdown from "./AccountPickerDropdown";
import BankHistoryFilterBar, { type ExportFormat } from "./BankHistoryFilterBar";
import TransactionsHistoryTable from "./TransactionsHistoryTable";
import AddChequeRow from "./AddChequeRow";
import NewTransactionDropdown from "./NewTransactionDropdown";

interface BankHistoryDashboardProps {
  data: BankAccountHistoryData;
  allAccounts: AccountPickerItem[];
}

const COLUMN_HEADER_BG = "#ECF0F4";

type SortColumn = "date" | "refType" | "payeeAccount" | "memo" | "left" | "right" | "balance";
type SortDir = "asc" | "desc";

export default function BankHistoryDashboard({ data, allAccounts }: BankHistoryDashboardProps) {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState<BankHistoryFilter>(DEFAULT_BANK_HISTORY_FILTER);
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState<{ column: SortColumn; dir: SortDir }>({ column: "date", dir: "desc" });
  const [settings, setSettings] = useState<BankHistorySettings>(() => loadBankHistorySettings());

  useEffect(() => {
    saveBankHistorySettings(settings);
  }, [settings]);

  const payees = useMemo(() => getUniquePayees(data.transactions), [data.transactions]);
  const isBankAccount = data.account.type === "Bank";
  const layout = getColumnLayout(data.account.type);

  const filtered = useMemo(() => {
    const combined: BankHistoryFilter = query.trim() ? { ...filter, find: query.trim() } : filter;
    return applyFilters(data.transactions, combined);
  }, [data.transactions, filter, query]);

  const withBalance = useMemo(
    () => computeRunningBalances(filtered, data.endingBalance),
    [filtered, data.endingBalance]
  );

  const sorted = useMemo(() => {
    type Row = BankTransaction & { runningBalance: number };
    const key = (r: Row): string | number => {
      switch (sort.column) {
        case "date": return r.date;
        case "refType": return (r.refNo ?? "") + "|" + r.type;
        case "payeeAccount": return (r.payee ?? "") + "|" + (r.linkedAccountName ?? "");
        case "memo": return r.memo ?? "";
        case "left": return r[layout.leftFieldKey] ?? 0;
        case "right": return r[layout.rightFieldKey] ?? 0;
        case "balance": return r.runningBalance;
        default: return r.date;
      }
    };
    const arr = [...withBalance];
    arr.sort((a, b) => {
      const av = key(a); const bv = key(b);
      if (av < bv) return sort.dir === "asc" ? -1 : 1;
      if (av > bv) return sort.dir === "asc" ? 1 : -1;
      return 0;
    });
    return arr;
  }, [withBalance, sort, layout.leftFieldKey, layout.rightFieldKey]);

  const pageSize = settings.rowsPerPage;
  const total = sorted.length;
  const pageStart = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const pageEnd = Math.min(page * pageSize, total);
  const paged = sorted.slice((page - 1) * pageSize, page * pageSize);

  const handleSort = (column: SortColumn) => {
    setSort((prev) => (prev.column === column ? { column, dir: prev.dir === "asc" ? "desc" : "asc" } : { column, dir: "desc" }));
    setPage(1);
  };

  const handleAccountChange = useCallback((id: string) => {
    navigate(`/accounting/chart-of-accounts/${id}/history`);
  }, [navigate]);

  const handlePrint = useCallback(() => {
    const rows: PrintRow[] = sorted;
    openPrintPreview(rows, {
      companyName: "Your Company",
      accountName: data.account.name,
      accountId: data.account.id,
      accountType: data.account.type,
      layout,
    });
  }, [sorted, data.account.name, data.account.id, data.account.type, layout]);

  const handleExport = useCallback((format: ExportFormat) => {
    const meta = {
      accountName: data.account.name,
      accountId: data.account.id,
      accountType: data.account.type,
      layout,
    };
    const rows: ExportRow[] = sorted;
    if (format === "csv") exportAsCsv(rows, meta);
    else exportAsXlsx(rows, meta);
  }, [sorted, data.account.name, data.account.id, data.account.type, layout]);

  const handleSaveTransaction = useCallback((updated: BankTransaction) => {
    alert(`Would save transaction ${updated.id}:\n${JSON.stringify(updated, null, 2)}`);
  }, []);

  const handleDeleteTransaction = useCallback((id: string) => {
    alert(`Would delete transaction ${id}`);
  }, []);

  const handleEditTransactionFull = useCallback((id: string) => {
    alert(`Would open full edit modal for transaction ${id}`);
  }, []);

  const handleBackToCOA = useCallback(() => navigate("/accounting/chart-of-accounts"), [navigate]);
  const handleBankTransactions = useCallback(() => navigate("/accounting/bank-transactions"), [navigate]);
  const handleReconcile = useCallback(() => navigate("/accounting/reconcile"), [navigate]);

  const colW = (key: string): React.CSSProperties => {
    const w = BANK_HISTORY_COL_WIDTHS[key];
    return w == null ? {} : { width: w };
  };

  const renderSortHeader = (column: SortColumn, label: string, align: "left" | "right" | "center" = "left") => {
    const isActive = sort.column === column;
    const activeArrow = sort.dir === "asc" ? "▲" : "▼";
    const alignClass = align === "right" ? "text-right" : align === "center" ? "text-center" : "text-left";
    const justify = align === "right" ? "justify-end" : align === "center" ? "justify-center" : "justify-start";
    return (
      <th
        onClick={() => handleSort(column)}
        className={`${settings.compact ? "py-1.5" : "py-2.5"} px-3.5 ${alignClass} text-[11px] font-bold text-ink2 uppercase tracking-wider whitespace-nowrap cursor-pointer select-none group hover:bg-[#DDE3EB]`}
      >
        <span className={`inline-flex items-center gap-1 ${justify}`}>
          <span>{label}</span>
          <span className={`text-[9px] leading-none ${isActive ? "text-ink opacity-100" : "text-ink3 opacity-0 group-hover:opacity-50"}`} aria-hidden="true">
            {isActive ? activeArrow : "▼"}
          </span>
        </span>
      </th>
    );
  };

  return (
    <div>
      <div className="sticky -top-7 z-20 bg-white -mx-4 md:-mx-8 px-4 md:px-8 pt-8 pb-0 no-print">
        <BackLink to="/accounting/chart-of-accounts" label="Back to chart of accounts" className="mb-1 -ml-2" />
        <div className="text-[12.5px] text-ink3 mb-2 font-medium">
          <span onClick={handleBackToCOA} className="cursor-pointer text-teal-dark">Accounting</span>
          <span className="mx-1.5">/</span>
          <span onClick={handleBackToCOA} className="cursor-pointer text-teal-dark">Chart of accounts</span>
          <span className="mx-1.5">/</span>
          <span className="text-ink">{data.account.name}</span>
        </div>

        <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-3 mb-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-extrabold m-0 text-ink -tracking-[0.3px]">{data.account.name}</h1>
            <div className="text-[12.5px] text-ink3 mt-1 font-medium">
              Account {data.account.id} &middot; {data.account.type} &middot; {data.transactions.length} transactions
            </div>
          </div>
          <div className="flex gap-2.5">
            {isBankAccount && (
              <button type="button" onClick={handleBankTransactions} className="h-9 px-4 rounded-lg border-[1.5px] border-teal-dark bg-white text-teal-dark text-[12.5px] font-bold cursor-pointer hover:bg-[#F4FAF7]">
                Bank transactions
              </button>
            )}
            {isBankAccount && (
              <button type="button" onClick={handleReconcile} className="h-9 px-5 rounded-lg bg-teal-dark text-white text-[12.5px] font-bold border-none cursor-pointer hover:brightness-110">
                Reconcile
              </button>
            )}
            {!isBankAccount && (
              <NewTransactionDropdown
                triggerLabel="+ New transaction"
                triggerClassName="h-9 px-5 rounded-lg bg-teal-dark text-white text-[12.5px] font-bold border-none cursor-pointer hover:brightness-110 inline-flex items-center gap-2"
                alignRight
              />
            )}
          </div>
        </div>

        <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4 py-3 border-b border-lineSoft mb-3">
          <AccountPickerDropdown accounts={allAccounts} selectedId={data.account.id} onSelect={handleAccountChange} />
          {isBankAccount && (
            <div className="flex flex-col">
              <span className="text-[11px] font-semibold text-ink2">Bank Balance</span>
              <span className="text-[14px] font-bold text-ink tabular-nums">{fmtCurrency(data.account.bankBalance)}</span>
            </div>
          )}
          <div className="text-right md:ml-auto">
            <div className="text-[11px] font-bold text-ink3 uppercase tracking-wider mb-0.5">Ending balance</div>
            <div className={`text-[24px] font-extrabold tabular-nums -tracking-[0.01em] ${data.endingBalance < 0 ? "text-[#B33E1A]" : "text-ink"}`}>
              {fmtCurrency(data.endingBalance)}
            </div>
          </div>
        </div>

        {data.transactions.some((t) => t.reconcileStatus === "unreconciled") && (
          <button
            type="button"
            onClick={() => setFilter({ ...filter, reconcileStatus: "unreconciled" })}
            className="inline-flex items-center gap-2 bg-vela-fade border border-vela-wash rounded-full pl-1.5 pr-2 py-1 text-left cursor-pointer hover:brightness-95 mb-3"
          >
            <span className="w-5 h-5 rounded-full bg-gradient-to-br from-[#7C3AED] to-[#6B46C1] text-white text-[10px] font-extrabold inline-flex items-center justify-center flex-shrink-0">V</span>
            <span className="text-[11.5px] text-vela-deep font-semibold whitespace-nowrap">
              <strong className="font-bold">Review needed</strong> &middot; {data.transactions.filter((t) => t.reconcileStatus === "unreconciled").length} unreconciled
            </span>
            <span className="bg-vela-deep text-white rounded-full px-2.5 py-0.5 text-[10.5px] font-semibold ml-1">Review</span>
          </button>
        )}

        <BankHistoryFilterBar
          query={query}
          onQueryChange={(q) => { setQuery(q); setPage(1); }}
          filter={filter}
          onFilterChange={(f) => { setFilter(f); setPage(1); }}
          payees={payees}
          settings={settings}
          onSettingsChange={(s) => { setSettings(s); setPage(1); }}
          onExport={handleExport}
          onPrint={handlePrint}
        />

        <div className="hidden md:block mt-3 -mx-4 md:-mx-8" style={{ background: COLUMN_HEADER_BG, borderTop: "1px solid #D8DEE6", borderBottom: "1px solid #D8DEE6" }}>
          <div className="px-4 md:px-8">
            <table className="w-full border-collapse" style={{ tableLayout: "fixed" }}>
              <colgroup>
                <col style={colW("date")} />
                <col style={colW("refType")} />
                <col style={colW("payeeAccount")} />
                {settings.columns.memo && <col style={colW("memo")} />}
                <col style={colW("payment")} />
                <col style={colW("deposit")} />
                {settings.columns.attachments && <col style={colW("attachments")} />}
                {settings.columns.reconcileStatus && <col style={colW("check")} />}
                {settings.columns.runningBalance && <col style={colW("balance")} />}
              </colgroup>
              <thead>
                <tr>
                  {renderSortHeader("date", "Date", "left")}
                  {renderSortHeader("refType", "Ref no / Type", "left")}
                  {renderSortHeader("payeeAccount", "Payee / Account", "left")}
                  {settings.columns.memo && renderSortHeader("memo", "Memo", "left")}
                  {renderSortHeader("left", layout.leftLabel, "right")}
                  {renderSortHeader("right", layout.rightLabel, "right")}
                  {settings.columns.attachments && (
                    <th className={`${settings.compact ? "py-1.5" : "py-2.5"} px-3.5 text-center text-[11px] font-bold text-ink2 uppercase tracking-wider`}>Att.</th>
                  )}
                  {settings.columns.reconcileStatus && (
                    <th className={`${settings.compact ? "py-1.5" : "py-2.5"} px-3.5 text-center text-[11px] font-bold text-ink2 uppercase tracking-wider`}>✓</th>
                  )}
                  {settings.columns.runningBalance && renderSortHeader("balance", layout.balanceLabel, "right")}
                </tr>
              </thead>
            </table>
          </div>
        </div>
      </div>

      <div className="print-area">
        {isBankAccount && <AddChequeRow />}
        <TransactionsHistoryTable
          rows={paged}
          layout={layout}
          settings={settings}
          pageStart={pageStart}
          pageEnd={pageEnd}
          total={total}
          page={page}
          pageSize={pageSize}
          onPrev={() => setPage((p) => Math.max(1, p - 1))}
          onNext={() => setPage((p) => (p * pageSize < total ? p + 1 : p))}
          onSaveTransaction={handleSaveTransaction}
          onDeleteTransaction={handleDeleteTransaction}
          onEditTransactionFull={handleEditTransactionFull}
        />
      </div>
    </div>
  );
}